import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { ArrowRight, CheckCircle2, Truck, ShieldCheck, Headphones, Sparkles, Mail, MessageCircle, CreditCard, ShoppingBag, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProductCard } from "@/components/shop/ProductCard";
import { fetchProducts, type ShopifyProduct } from "@/lib/shopify";
import { toast } from "sonner";
import hero from "@/assets/hero.jpg";
import gadgets from "@/assets/cat-gadgets.jpg";
import kitchen from "@/assets/cat-kitchen.jpg";
import watches from "@/assets/cat-watches.jpg";
import toys from "@/assets/cat-toys.jpg";

export const Route = createFileRoute("/")({
  component: HomePage,
});

const categories = [
  { name: "Home Gadgets", img: gadgets, q: "home" },
  { name: "Kitchen Essentials", img: kitchen, q: "kitchen" },
  { name: "Smart Watches", img: watches, q: "watch" },
  { name: "Kids Toys", img: toys, q: "toy" },
];

const reasons = [
  { icon: CheckCircle2, label: "Trusted Store in Pakistan" },
  { icon: ShieldCheck, label: "Cash on Delivery Available" },
  { icon: Truck, label: "Fast Nationwide Shipping" },
  { icon: Headphones, label: "Quality Products Guaranteed" },
];

function HomePage() {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts(undefined, 8)
      .then(setProducts)
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Helmet>
        <title>Trendora Marts — Smart Shopping Starts Here</title>
        <meta name="description" content="Trending gadgets, smart watches, kitchen essentials & kids toys. Cash on Delivery across Pakistan." />
        <meta property="og:title" content="Trendora Marts — Smart Shopping Starts Here" />
        <meta property="og:description" content="Trending Gadgets, Watches & Toys – Delivered Across Pakistan" />
      </Helmet>
      {/* HERO */}
      <section className="relative w-full overflow-hidden bg-secondary">
        <div className="container mx-auto px-4 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-[--gold]/40 text-[--navy] text-xs uppercase tracking-widest mb-6 shadow-soft">
              <Sparkles className="h-3 w-3 text-[--gold]" /> Trendora Marts
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-[--navy] leading-tight">
              Pakistan's <span className="text-[--gold]">Trusted Store</span> For <br />
              Trending <span className="text-[--gold]">Gadgets</span>, Watches & Toys
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl">
              Discover quality products, fast delivery and unbeatable value — delivered across Pakistan.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild variant="gold" size="lg">
                <Link to="/shop">Shop Now <ArrowRight className="h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-[--navy] text-[--navy] hover:bg-[--navy] hover:text-white">
                <Link to="/about">Our Story</Link>
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[--teal]" /> Cash on Delivery</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[--teal]" /> 7-Day Returns</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[--teal]" /> All Pakistan Shipping</span>
            </div>
          </div>
          <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-elegant bg-white">
            <img src={hero} alt="Premium lifestyle products" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 ring-1 ring-[--gold]/20 rounded-2xl pointer-events-none" />
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-white py-14 border-y border-border">
        <div className="container mx-auto px-4 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { Icon: Truck, title: "Fast Delivery", sub: "3–5 working days" },
            { Icon: CreditCard, title: "Secure Payments", sub: "Cash on Delivery" },
            { Icon: ShoppingBag, title: "Easy Shopping", sub: "Hassle-free checkout" },
            { Icon: Star, title: "Trusted Quality", sub: "Verified products" },
          ].map(({ Icon, title, sub }) => (
            <div key={title} className="p-6 rounded-2xl bg-white border border-border hover:border-[--gold] hover-lift transition-colors text-center sm:text-left flex flex-col sm:flex-row items-center gap-4">
              <span className="h-12 w-12 rounded-xl bg-[--teal]/10 text-[--teal] grid place-items-center shrink-0">
                <Icon className="h-6 w-6" />
              </span>
              <div>
                <p className="font-semibold text-[--navy]">{title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-[--teal] uppercase text-xs tracking-[0.3em] mb-3 font-semibold">Shop by Category</p>
          <h2 className="text-3xl sm:text-4xl font-serif text-[--navy]">Featured Categories</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {categories.map((c) => (
            <Link
              key={c.name}
              to="/shop"
              search={{ q: c.q }}
              className="group relative aspect-[4/5] rounded-xl overflow-hidden bg-card border border-border hover:border-[--teal] hover-lift block"
            >
              <img src={c.img} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[--navy]/85 via-[--navy]/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="text-white font-serif text-lg sm:text-xl">{c.name}</h3>
                <span className="text-[--gold] text-xs uppercase tracking-widest inline-flex items-center gap-1 mt-1">
                  Shop <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BEST SELLERS */}
      <section className="bg-secondary py-20">
        <div className="container mx-auto px-4">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="text-[--teal] uppercase text-xs tracking-[0.3em] mb-3 font-semibold">Best Sellers</p>
              <h2 className="text-3xl sm:text-4xl font-serif text-[--navy]">Customer Favorites</h2>
            </div>
            <Button asChild variant="dark"><Link to="/shop">View All <ArrowRight className="h-4 w-4" /></Link></Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="aspect-square bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-lg bg-background">
              <p className="text-muted-foreground">No products yet. Tell us what you'd like to sell and we'll add it instantly.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.slice(0, 8).map((p) => <ProductCard key={p.node.id} product={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-[--navy] py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-[--gold] uppercase text-xs tracking-[0.3em] mb-3 font-semibold">Why Choose Us</p>
            <h2 className="text-3xl sm:text-4xl font-serif text-white">Trusted by Hundreds Across Pakistan</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {reasons.map((r) => (
              <div key={r.label} className="text-center p-8 rounded-xl bg-white/5 border border-white/10 hover:border-[--gold] hover-lift backdrop-blur-sm">
                <div className="h-14 w-14 mx-auto rounded-full bg-[--gold]/15 grid place-items-center mb-4">
                  <r.icon className="h-7 w-7 text-[--gold]" />
                </div>
                <p className="font-medium text-white">{r.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS PREVIEW */}
      <section className="bg-secondary py-20 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-[--teal] uppercase text-xs tracking-[0.3em] mb-3 font-semibold">Reviews</p>
            <h2 className="text-3xl sm:text-4xl font-serif text-[--navy]">What Our Customers Say</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { q: "Amazing quality! Delivery was fast.", n: "Ali Raza", c: "Lahore" },
              { q: "Very satisfied with my purchase.", n: "Ayesha Khan", c: "Karachi" },
              { q: "Product exactly as shown. Highly recommended.", n: "Usman Tariq", c: "Islamabad" },
            ].map((t, i) => (
              <div key={i} className="p-8 rounded-xl bg-card border border-border hover-lift">
                <div className="flex text-[--gold] mb-4 text-lg">{"★★★★★"}</div>
                <p className="text-foreground italic mb-6 leading-relaxed">"{t.q}"</p>
                <div>
                  <p className="font-semibold text-[--navy]">{t.n}</p>
                  <p className="text-sm text-muted-foreground">{t.c}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button asChild variant="gold"><Link to="/testimonials">Read All Reviews</Link></Button>
          </div>
        </div>
      </section>

      {/* WHATSAPP CTA */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto rounded-2xl bg-card border border-border p-10 sm:p-14 flex flex-col sm:flex-row items-center gap-6 shadow-soft">
          <div className="h-16 w-16 rounded-full bg-[#25D366] grid place-items-center shrink-0 text-white">
            <MessageCircle className="h-8 w-8" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-2xl sm:text-3xl font-serif mb-2 text-[--navy]">Need Help? Chat With Us Instantly</h2>
            <p className="text-muted-foreground">Our team is ready to assist you on WhatsApp.</p>
          </div>
          <a
            href={`https://wa.me/923707851120?text=${encodeURIComponent("Hello Trendora Marts, I need help with my order.")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[--teal] text-white font-semibold px-6 py-3 rounded-full hover:bg-[--navy] transition-colors"
          >
            <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
          </a>
        </div>
      </section>

      {/* FAQ */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[--teal] uppercase text-xs tracking-[0.3em] mb-3 font-semibold">FAQs</p>
            <h2 className="text-3xl sm:text-4xl font-serif text-[--navy]">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {[
              { q: "Do you offer Cash on Delivery?", a: "Yes! Cash on Delivery is available on every order across Pakistan." },
              { q: "How long does delivery take?", a: "Orders are delivered within 3–5 working days nationwide." },
              { q: "How do I place an order?", a: "Add products to cart and click 'Order on WhatsApp' — or use 'Buy Now' on any product for instant WhatsApp checkout." },
              { q: "What is your return policy?", a: "We offer easy 7-day returns for damaged or defective items. Contact us on WhatsApp for support." },
              { q: "Are the products original?", a: "Absolutely. Every product is quality-checked before dispatch to ensure you receive genuine, working items." },
            ].map((f, i) => (
              <details key={i} className="group bg-card border border-border rounded-lg p-5 open:shadow-soft">
                <summary className="flex justify-between items-center cursor-pointer font-medium text-[--navy]">
                  {f.q}
                  <span className="text-[--teal] text-xl transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      {/* NEWSLETTER */}


      <section className="container mx-auto px-4 py-20">
        <div className="max-w-3xl mx-auto text-center bg-secondary rounded-2xl p-10 sm:p-14 border border-border">
          <Mail className="h-10 w-10 text-[--teal] mx-auto mb-4" />
          <h2 className="text-3xl font-serif mb-3 text-[--navy]">Join Our Newsletter</h2>
          <p className="text-muted-foreground mb-8">Get exclusive deals and new arrivals straight to your inbox.</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              toast.success("Subscribed! Welcome to Trendora Marts.");
              (e.target as HTMLFormElement).reset();
            }}
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <Input type="email" required placeholder="Enter your email" maxLength={120} className="bg-background" />
            <Button type="submit" variant="teal">Subscribe</Button>
          </form>
        </div>
      </section>
    </>
  );
}
